import React from 'react';
import Modal from 'react-modal';
import { apiFollowUser, apiUnfollowUser } from '../api';
import { getProfileImageUrl } from '../utils/profileImage';

type User = {
  userId: number;
  userName: string;
  email: string;
  youFollowed: boolean;
  profileUrl?: string | null;
};

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  title: string;
  users: User[] | undefined | null;
  removeOnUnfollow?: boolean;
  user: any;
  refreshCounts?: () => void;
};

const customStyles = {
  content: {
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80vw',
    height: 'auto',
    maxWidth: '900px',
    maxHeight: '90vh',
    borderRadius: '22px',
    background: '#f9c5b4',
    border: 'none',
    padding: 0,
    overflow: 'auto',
    boxShadow: '0 8px 40px #2224',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'absolute',
  } as React.CSSProperties,
};

function getButtonStyles(isUnfollow: boolean) {
  return {
    background: isUnfollow ? '#ff884d' : '#7bf1d2',
    color: isUnfollow ? '#001' : '#000',
  };
}

const FollowsModal: React.FC<Props> = ({
                                         isOpen,
                                         onRequestClose,
                                         title,
                                         users,
                                         removeOnUnfollow = false,
                                         user,
                                         refreshCounts,
                                       }) => {
  const [userStates, setUserStates] = React.useState<User[]>([]);

  React.useEffect(() => {
    setUserStates(Array.isArray(users) ? users : []);
  }, [users, isOpen]);

  const handleFollow = async (userId: number) => {
    try {
      await apiFollowUser(userId, 'Bearer ' + user.token);
      setUserStates(states =>
        states.map(u =>
          u.userId === userId ? { ...u, youFollowed: true } : u
        )
      );
      refreshCounts && refreshCounts();
    } catch {}
  };

  const handleUnfollow = async (userId: number) => {
    try {
      await apiUnfollowUser(userId, 'Bearer ' + user.token);
      if (removeOnUnfollow) {
        setUserStates(states => states.filter(u => u.userId !== userId));
      } else {
        setUserStates(states =>
          states.map(u =>
            u.userId === userId ? { ...u, youFollowed: false } : u
          )
        );
      }
      refreshCounts && refreshCounts();
    } catch {}
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={customStyles}
      ariaHideApp={false}
      className="FollowsModal__modal"
      overlayClassName="customModalOverlay"
    >
      <div
        style={{
          width: '100%',
          position: 'relative',
          padding: 0,
        }}
      >
        {/* Top bar */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 40px 18px 40px',
            position: 'relative',
          }}
        >
          <h2
            style={{
              margin: 0,
              textAlign: 'center',
              fontWeight: 800,
              fontSize: '2.45rem',
              letterSpacing: '-1px',
              flex: 1,
              color: '#111',
              lineHeight: 1,
            }}
          >
            {title}
          </h2>
          <button
            onClick={onRequestClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              right: 32,
              top: 32,
              background: 'none',
              border: 'none',
              fontSize: 32,
              fontWeight: 'bold',
              color: '#b44a5c',
              cursor: 'pointer',
              lineHeight: 1,
              padding: 0,
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            &times;
          </button>
        </div>

        {/* User list */}
        <div
          style={{
            width: '100%',
            padding: '0 40px 25px 40px',
            maxHeight: '490px',
            overflowY: 'auto',
            boxSizing: 'border-box',
          }}
        >
          {userStates.length === 0 ? (
            <div
              style={{ textAlign: 'center', color: '#777', margin: '32px 0' }}
            >
              No users found.
            </div>
          ) : (
            <div>
              {userStates.map((user, idx) => {
                const isUnfollow = !!user.youFollowed;
                const { background, color } = getButtonStyles(isUnfollow);
                return (
                  <div
                    className="user-row"
                    key={user.userId || idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '14px 0',
                      borderBottom:
                        idx === userStates.length - 1
                          ? 'none'
                          : '1px solid rgba(100,0,0,0.09)',
                      width: '100%',
                      background: 'transparent',
                      gap: 0,
                    }}
                  >
                    <img
                      src={getProfileImageUrl(user.profileUrl)}
                      alt={user.userName}
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        marginRight: 16,
                        border: `2px solid ${background}`,
                        flexShrink: 0,
                        objectFit: 'cover',
                      }}
                    />
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 16,
                          color: '#111',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginBottom: 2,
                        }}
                      >
                        {user.userName}
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          color: '#666',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.email}
                      </span>
                    </div>
                    <div
                      style={{
                        width: 110,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {isUnfollow ? (
                        <button
                          style={{
                            border: 'none',
                            borderRadius: 7,
                            padding: '8px 0px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: 15,
                            minWidth: 92,
                            width: 92,
                            marginLeft: 8,
                            background,
                            color,
                            transition: 'background 0.15s',
                          }}
                          onClick={() => handleUnfollow(user.userId)}
                        >
                          Unfollow
                        </button>
                      ) : (
                        <button
                          style={{
                            border: 'none',
                            borderRadius: 7,
                            padding: '8px 0px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: 15,
                            minWidth: 92,
                            width: 92,
                            marginLeft: 8,
                            background,
                            color,
                            transition: 'background 0.15s',
                          }}
                          onClick={() => handleFollow(user.userId)}
                        >
                          Follow
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FollowsModal;